import {
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  List,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useMemo, useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import DeviceInfoModalContent from '@components/Modal/Content/DeviceInfoModalContent';
import TextInputBox from '@components/TextInputBox';

import { TERRA_CONSENT } from '@constants/consents';

import { useGetConsents } from '@hooks/api/consent';
import { useGetTerraConnections } from '@hooks/api/terra';
import { useModal } from '@hooks/useModal';
import { useSession } from '@hooks/useSession';

import { getConnectAppName } from '@utils/device-brand';

import ConsentForm from '@views/ManageAccount/Consent/ConsentForm';

import type {
  TerraIntegration as ITerraIntegration,
  TerraConnection,
} from 'generated/types';

import TerraIntegration from './TerraIntegration';

const messages = defineMessages({
  devices: {
    id: 'devicesMain.devices',
    defaultMessage: 'My Health Devices',
  },
  myDevices: {
    id: 'devicesMain.myDevices',
    defaultMessage: 'My Health Devices',
  },
  statusNoDevice: {
    id: 'devicesMain.statusNoDevice',
    defaultMessage: 'No devices connected',
  },
  addDevices: {
    id: 'devicesMain.addDevices',
    defaultMessage: 'Add Health Devices',
  },
  search: {
    id: 'GLOBAL.search',
    defaultMessage: 'Search',
  },
  deviceInfo: {
    id: 'devicesMain.deviceInfo',
    defaultMessage: 'What data comes from my health devices?',
  },
});

function TerraMain() {
  const { formatMessage } = useIntl();
  const { closeModal, openModal, setContent } = useModal();
  const {
    state: { activeProfile },
  } = useSession();
  const [searchText, setSearchText] = useState<string>('');
  const { palette } = useTheme();

  const { data: consentsData, loading: consentsLoading } = useGetConsents();

  const { data: terraConnectionsData, loading: terraConnectionsLoading } =
    useGetTerraConnections({
      vitall_patient_id: activeProfile?.vitall_patient_id ?? '',
    });

  const terraConsent = consentsData.find((c) => c.hasCategory(TERRA_CONSENT));
  const hasConsented = terraConsent?.isActive();

  // Get connected devices
  const connectedDevices = terraConnectionsData?.filter(
    (c): c is ITerraIntegration & { connection: TerraConnection } =>
      Boolean(c.connection)
  );
  const connections = connectedDevices?.map(({ integration }) => integration);

  // Get unconnected devices
  const unconnectedDevices = useMemo(() => {
    const regex = new RegExp(`.*${searchText}.*`, 'i');
    return terraConnectionsData?.filter(
      ({ integration, name }) =>
        // Match what the row actually shows, so searching "connect" finds
        // Garmin Connect™.
        !connections?.includes(integration) &&
        regex.test(getConnectAppName(integration, name))
    );
  }, [connections, searchText, terraConnectionsData]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchText(value);
  };

  const handleOpenDeviceInfoModal = () => {
    openModal({
      width: 700,
      props: {
        height: 'fit-content',
      },
      children: null,
    });
    setContent({
      children: <DeviceInfoModalContent onClose={closeModal} />,
    });
  };

  const loading = consentsLoading || terraConnectionsLoading;

  return (
    <ContentLoader loading={loading}>
      {/* eslint-disable-next-line no-negated-condition*/}
      {!hasConsented ? (
        <Container sx={{ backgroundColor: palette.background.paper, py: 2 }}>
          <ConsentForm terra_consent user_id={activeProfile?.user_id} />
        </Container>
      ) : null}

      {hasConsented ? (
        <Container
          maxWidth="md"
          sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}
          disableGutters
        >
          <Card>
            <CardHeader
              action={
                <Typography
                  color="info.main"
                  onClick={handleOpenDeviceInfoModal}
                  variant="body2"
                  sx={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {formatMessage(messages.deviceInfo)}
                </Typography>
              }
              sx={{
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                rowGap: 2,
                '& .MuiCardHeader-action': {
                  alignSelf: 'center',
                },
              }}
              title={formatMessage(messages.myDevices)}
            />
            <Divider />
            {connectedDevices?.length ? (
              <List>
                {connectedDevices.map((device, index) => {
                  return (
                    <React.Fragment
                      key={`terra-connection-${device.connection.terra_connection_id}`}
                    >
                      <TerraIntegration
                        connection={device.connection}
                        integration={device}
                      />
                      {index < connectedDevices.length - 1 ? <Divider /> : null}
                    </React.Fragment>
                  );
                })}
              </List>
            ) : (
              <CardContent>
                {formatMessage(messages.statusNoDevice)}
              </CardContent>
            )}
          </Card>

          <Card>
            <CardHeader
              action={
                <TextInputBox
                  onChange={handleChange}
                  placeholder={formatMessage(messages.search)}
                  value={searchText}
                />
              }
              title={formatMessage(messages.addDevices)}
            />
            <Divider />
            <List>
              {unconnectedDevices?.map((device, index) => {
                const { integration } = device;

                return (
                  <React.Fragment key={integration}>
                    <TerraIntegration integration={device} />
                    {index < unconnectedDevices.length - 1 ? <Divider /> : null}
                  </React.Fragment>
                );
              })}
            </List>
          </Card>
        </Container>
      ) : null}
    </ContentLoader>
  );
}

export default withErrorBoundary(TerraMain, {
  FallbackComponent: ErrorBoundaryFallback,
});
